import { useReadQuery } from "@apollo/client";
import { GridLegacy as Grid, Box, Typography, Paper } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { StatusDonutChart, SubmissionStatusId } from "./StatusDonutChart";
import { TenantDonutChart } from "./TenantDonutChart";
import { StateBarChart } from "./StateBarChart";
import { AssigneeDonutChart } from "./AssigneeDonutChart";
import { useWasteFulfillmentSummaryContext } from "../WasteFulfillmentSummaryContext";

interface SummaryProps {
  /**
   * Whether the "only overdue" filter is active.
   * Used for highlighting the Overdue stat.
   */
  onlyOverdue: boolean;
  /**
   * Callback when the Overdue stat is clicked.
   */
  onOverdueClick: () => void;
}

/**
 * Strip surrounding quotes from a string if present.
 * This is needed because omnisearch values with spaces are quoted,
 * but the quotes are preserved when retrieved via getOmnisearchValue.
 */
function stripQuotes(value: string | undefined): string | undefined {
  if (!value) return value;
  if ((value.startsWith('"') && value.endsWith('"')) || 
      (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1);
  }
  return value;
}

/**
 * Summary component that displays key metrics and charts for the waste fulfillment dashboard.
 *
 * This component uses useReadQuery to read data from the context's QueryRef.
 * useReadQuery automatically shows old data while new data loads in the background,
 * preventing skeleton flash on filter changes.
 *
 * Data fetching is handled by WasteFulfillmentSummaryProvider using useBackgroundQuery.
 */
export function Summary({ onlyOverdue, onOverdueClick }: SummaryProps) {
  const { getOmnisearchValue, setOmnisearch } = useOmnisearchInput({
    isURLDriven: true,
  });

  // Get the query ref from context - this uses useBackgroundQuery under the hood
  // which prevents re-suspension on filter changes
  // isPending is true while filters are transitioning (deferred value hasn't caught up yet)
  const { summaryQueryRef, isPending } = useWasteFulfillmentSummaryContext();

  // useReadQuery reads from the query ref without triggering loading states on refetch
  // It automatically shows old data while new data loads in the background
  const { data } = useReadQuery(summaryQueryRef);

  const summary = data?.wasteSubmissionsStaffSummary;

  /**
   * Check if a value is a single selection (not multi-value).
   * Multi-value filters use either pipe (|) or comma (,) as separators.
   * This is a type guard that narrows the type to string.
   */
  const isSingleValue = (value: string | undefined): value is string => {
    if (!value) return false;
    return !value.includes("|") && !value.includes(",");
  };

  // Derive selected status from omnisearch
  const selectedStatusId = useMemo((): SubmissionStatusId | null => {
    const status = stripQuotes(getOmnisearchValue("status"));
    if (isSingleValue(status)) {
      return status as SubmissionStatusId;
    }
    return null;
  }, [getOmnisearchValue]);

  // Derive selected tenant from omnisearch
  const selectedTenantName = useMemo((): string | null => {
    const tenantName = stripQuotes(getOmnisearchValue("tenantName"));
    if (isSingleValue(tenantName)) {
      return tenantName;
    }
    return null;
  }, [getOmnisearchValue]);

  // Derive selected state from omnisearch
  const selectedState = useMemo((): string | null => {
    const state = stripQuotes(getOmnisearchValue("state"));
    if (isSingleValue(state)) {
      return state;
    }
    return null;
  }, [getOmnisearchValue]);

  // Derive selected assignee from omnisearch
  const selectedAssigneeEmail = useMemo((): string | null => {
    const staffAssignee = stripQuotes(getOmnisearchValue("staffAssignee"));
    if (isSingleValue(staffAssignee)) {
      return staffAssignee;
    }
    return null;
  }, [getOmnisearchValue]);

  // Handle status click from donut chart
  const handleStatusClick = useCallback(
    (statusId: SubmissionStatusId) => {
      // Get current filter values to preserve
      const currentFilters = {
        tenantName: getOmnisearchValue("tenantName"),
        state: getOmnisearchValue("state"),
        assignedToMe: getOmnisearchValue("assignedToMe"),
      };

      // If clicking the same status, clear the filter
      if (statusId === selectedStatusId) {
        setOmnisearch(
          stringifyOmnisearch({
            ...currentFilters,
            status: undefined,
          })
        );
        return;
      }

      // Apply the new status filter
      setOmnisearch(
        stringifyOmnisearch({
          ...currentFilters,
          status: statusId,
        })
      );
    },
    [getOmnisearchValue, setOmnisearch, selectedStatusId]
  );

  // Handle tenant click from donut chart
  const handleTenantClick = useCallback(
    (tenantName: string) => {
      // Get current filter values to preserve
      const currentFilters = {
        status: getOmnisearchValue("status"),
        state: getOmnisearchValue("state"),
        assignedToMe: getOmnisearchValue("assignedToMe"),
      };

      // If clicking the same tenant, clear the filter
      if (tenantName === selectedTenantName) {
        setOmnisearch(
          stringifyOmnisearch({
            ...currentFilters,
            tenantName: undefined,
          })
        );
        return;
      }

      // Apply the new tenant filter
      setOmnisearch(
        stringifyOmnisearch({
          ...currentFilters,
          tenantName,
        })
      );
    },
    [getOmnisearchValue, setOmnisearch, selectedTenantName]
  );

  // Handle state click from bar chart
  const handleStateClick = useCallback(
    (state: string) => {
      // Get current filter values to preserve
      const currentFilters = {
        status: getOmnisearchValue("status"),
        tenantName: getOmnisearchValue("tenantName"),
        assignedToMe: getOmnisearchValue("assignedToMe"),
      };

      // If clicking the same state, clear the filter
      if (state === selectedState) {
        setOmnisearch(
          stringifyOmnisearch({
            ...currentFilters,
            state: undefined,
          })
        );
        return;
      }

      // Apply the new state filter
      setOmnisearch(
        stringifyOmnisearch({
          ...currentFilters,
          state,
        })
      );
    },
    [getOmnisearchValue, setOmnisearch, selectedState]
  );

  // Handle assignee click from donut chart
  const handleAssigneeClick = useCallback(
    (assigneeEmail: string) => {
      // Get current filter values to preserve
      const currentFilters = {
        status: getOmnisearchValue("status"),
        tenantName: getOmnisearchValue("tenantName"),
        state: getOmnisearchValue("state"),
        assignedToMe: getOmnisearchValue("assignedToMe"),
      };

      // If clicking the same assignee, clear the filter
      if (assigneeEmail === selectedAssigneeEmail) {
        setOmnisearch(
          stringifyOmnisearch({
            ...currentFilters,
            staffAssignee: undefined,
          })
        );
        return;
      }

      // Apply the new assignee filter
      setOmnisearch(
        stringifyOmnisearch({
          ...currentFilters,
          staffAssignee: assigneeEmail,
        })
      );
    },
    [getOmnisearchValue, setOmnisearch, selectedAssigneeEmail]
  );

  return (
    <Grid
      container
      spacing={3}
      sx={{
        // Show reduced opacity while filters are transitioning
        // This indicates to users that data is being refetched
        opacity: isPending ? 0.6 : 1,
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      {/* Key Metrics Row */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={600}>
                  {summary?.total.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Submissions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                textAlign="center"
                onClick={onOverdueClick}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  p: 1,
                  mx: -1,
                  transition: "background-color 0.2s",
                  backgroundColor: onlyOverdue
                    ? "action.selected"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: onlyOverdue
                      ? "action.selected"
                      : "action.hover",
                  },
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={summary?.overdue ? "error.main" : "text.primary"}
                >
                  {summary?.overdue.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {onlyOverdue ? "Overdue (filtered)" : "Overdue"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Status Donut Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <StatusDonutChart
          data={summary}
          loading={false}
          onStatusClick={handleStatusClick}
          selectedStatusId={selectedStatusId}
        />
      </Grid>

      {/* Tenant Donut Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <TenantDonutChart
          data={summary}
          loading={false}
          onTenantClick={handleTenantClick}
          selectedTenantName={selectedTenantName}
        />
      </Grid>

      {/* State Bar Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <StateBarChart
          data={summary}
          loading={false}
          onStateClick={handleStateClick}
          selectedState={selectedState}
        />
      </Grid>

      {/* Assignee Donut Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <AssigneeDonutChart
          data={summary}
          loading={false}
          onAssigneeClick={handleAssigneeClick}
          selectedAssigneeEmail={selectedAssigneeEmail}
        />
      </Grid>
    </Grid>
  );
}
